import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import React, { useEffect } from 'react';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { SiteSubjectType } from '@dynamika-shared/types/subjects';
import { CloseIcon } from '@dynamika-shared/ui/icons';
import './index.scss';
import { UploadDrawerSteps } from '@components/SiteUserUpload/Components/UploadDrawerSteps';
import {
  CheckStatus,
  UploadDrawerStep,
} from '@components/SiteUserUpload/types/uploadTypes';
import {
  PolingResponse,
  UploadStatus,
  VerificationStepsItem,
} from 'src/store-redux/siteUpload/siteUploadStore.types';
import { twJoin } from '@utils/twMerge';
import { useParams } from 'react-router-dom';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { useSiteUploadDrawerHooks } from '@components/SiteUserUpload/helpers/useSiteUploadDrawerHooks';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { VisitStep } from './Components/Steps/VisitStep';
import { UploadFileStep } from './Components/Steps/UploadFileStep';
import { UploadValidationStep } from './Components/Steps/UploadValidationStep';
import { UploadFormEdtf } from './Components/uploadFormEDTF/UploadFormEDTF';
import { useSiteUploadSlice } from '@restore/siteUpload/siteUpload.slice';
import { useSiteSubjectsListSlice } from '@restore/SiteSubjectsList/SiteSubjectsList.slice';
import { useUserActivityTracker } from 'src/hooks/useUserActivityTracker';

type SiteUserUploadDrawer = {
  siteCode?: string;
  subject: SiteSubjectType | null | undefined;
  setSubject: (isToggle: null) => void;
};

export const SiteUserUploadDrawer: React.FC<SiteUserUploadDrawer> = ({
  subject,
  siteCode = '',
  setSubject,
}) => {
  const params = useParams();
  const [
    status,
    setStatus,
    uploadFile,
    clearData,
    polingData,
    setReupload,
    submitUpload,
    cancelUpload,
  ] = useSiteUploadSlice((state, actions) => [
    state.status,
    actions.setStatus,
    actions.uploadFile,
    actions.clearData,
    state.polingData,
    actions.setReupload,
    actions.submitUpload,
    actions.cancelUpload,
  ]);

  const { start, pause } = useUserActivityTracker();

  const data = useClinicalTrialContextSlice(state => state.data);

  const [onNewlyCreatedSubject] = useSiteSubjectsListSlice((state, actions) => [
    actions.onNewlyCreatedSubject,
  ]);

  const {
    additionalComment,
    askQuit,
    dirtyDrawerState,
    verificationStepsData,
    currentStep,
    file,
    visitName,
    disableConfirmUpload,
    reuploadDrawer,

    setAdditionalComment,
    setAskQuit,
    setDirtyDrawerState,
    setVerificationStepsData,
    setCurrentStep,
    setFile,
    setVisitName,
    setDisableConfirmUpload,
    setReuploadDrawer,
  } = useSiteUploadDrawerHooks();

  useEffect(() => {
    if (polingData) {
      const filterWarningItems = polingData.verificationSteps.filter(
        item => item.status === CheckStatus.WARNING
      );
      setVerificationStepsData(
        filterWarningItems as Partial<VerificationStepsItem>[]
      );
    }
  }, [polingData]);

  useEffect(() => {
    if (
      status === UploadStatus.FILE_POLING_SUCCESS ||
      status === UploadStatus.FINALIZE_UPLOAD
    ) {
      onNewlyCreatedSubject(subject!.code);
      status === UploadStatus.FINALIZE_UPLOAD && handleCloseDrawer(false);
    }
  }, [status]);

  const handleDrawerClick = () => {
    if (
      status === UploadStatus.INITIAL_VIEW &&
      file &&
      params.id &&
      params.siteId
    ) {
      pause();
      setDisableConfirmUpload(true);
      if (reuploadDrawer) {
        setReupload();
      }
      uploadFile({
        file,
        selectedClinicalTrial: params.id,
        selectedSite: params.siteId,
        additionalComment: additionalComment?.trim(),
      });

      setReuploadDrawer(false);
    }
    if (
      (status === UploadStatus.FILE_POLING_WARNING ||
        status === UploadStatus.FILE_POLING_SUCCESS) &&
      params.id &&
      params.siteId &&
      verificationStepsData &&
      subject
    ) {
      setDisableConfirmUpload(true);
      submitUpload({
        selectedClinicalTrial: params.id,
        selectedSite: params.siteId,
        verificationStepsData,
      });
    }
  };

  const handleCloseDrawer = (cancel = true) => {
    if (
      (status === UploadStatus.FILE_UPLOAD_IN_PROGRESS ||
        status === UploadStatus.FILE_POLING_ERROR ||
        status === UploadStatus.FILE_POLING_WARNING ||
        status === UploadStatus.FILE_POLING_SUCCESS) &&
      params.id &&
      params.siteId
    ) {
      cancel &&
        cancelUpload({
          selectedClinicalTrial: params.id,
          selectedSite: params.siteId,
        });
    }
    setSubject(null);
    setFile(undefined);
    start();
    clearData();
  };

  const onCloseDrawer = () => {
    if (dirtyDrawerState) {
      setAskQuit(true);
    } else {
      handleCloseDrawer();
    }
  };
  return (
    <>
      <Drawer
        data-testid='SiteUserUploadDrawer'
        title={
          <div className='px-3 flex flex-col items-center w-full'>
            <div className='mb-3 flex justify-between border-b border-brightGray pb-2 w-full'>
              <h2 className='flex leading-32 text-2.4rem truncate max-w-[100%] h-[44px] '>
                <TruncatedText
                  text={siteCode}
                  truncate
                  tooltip
                  className={'max-w-[150px] mr-1'}
                />

                <span className='text-spanishGray'>upload visit files for</span>

                <TruncatedText
                  text={subject?.code}
                  truncate
                  tooltip
                  className={'max-w-[400px] ml-1'}
                />
              </h2>

              <CloseIcon
                className='absolute right-2 top-2 h-3 w-3 cursor-pointer'
                onClick={onCloseDrawer}
              />
            </div>
            <UploadDrawerSteps
              currentStep={currentStep}
              hasFile={!!file}
              visitName={visitName}
            />
          </div>
        }
        id='upload-drawer'
        classNames={{
          header: '!p-0',
          body: 'overflow-y-hidden px-3 py-0 pr-2',
          content: 'rounded-[24px]',
        }}
        closeIcon={null}
        open={!!subject}
        width={928}
        placement='right'
        footer={
          <div className='flex justify-between mt-3'>
            <Button
              className='w-[48%] flex-none shrink-0'
              onClick={onCloseDrawer}
            >
              Cancel
            </Button>
            <Button
              disabled={disableConfirmUpload}
              className='w-[48%] flex-none shrink-0'
              onClick={handleDrawerClick}
              type='primary'
              data-testid='ok-modal-btn'
            >
              {status !== UploadStatus.INITIAL_VIEW
                ? 'Confirm Upload'
                : 'Upload'}
            </Button>
          </div>
        }
      >
        <div className='upload-drawer-body relative h-full'>
          {status === UploadStatus.INITIAL_VIEW && (
            <div className='uploadGifGroup'></div>
          )}
          <div
            className={twJoin(
              currentStep !== UploadDrawerStep.FileValidationStep &&
                'absolute z-50 flex h-full w-full items-center justify-center'
            )}
          >
            {currentStep === UploadDrawerStep.VisitStep && (
              <VisitStep
                setCurrentStep={setCurrentStep}
                currentStep={currentStep}
                subject={subject}
                setDirtyDrawerState={setDirtyDrawerState}
                setVisitName={setVisitName}
                data={data}
              />
            )}

            {currentStep === UploadDrawerStep.EdtfStep && (
              <UploadFormEdtf
                setCurrentStep={setCurrentStep}
                currentStep={currentStep}
              />
            )}
            {currentStep === UploadDrawerStep.FileUploadStep && (
              <UploadFileStep
                setCurrentStep={setCurrentStep}
                currentStep={currentStep}
                setFile={setFile}
                file={file}
                setDisableConfirmUpload={setDisableConfirmUpload}
                setAdditionalComment={setAdditionalComment}
                setReuploadDrawer={setReuploadDrawer}
              />
            )}
            {currentStep === UploadDrawerStep.FileValidationStep && (
              <UploadValidationStep
                setCurrentStep={setCurrentStep}
                setFile={setFile}
                setDisableConfirmUpload={setDisableConfirmUpload}
                setVerificationStepsData={setVerificationStepsData}
                verificationStepsData={verificationStepsData}
                polingData={polingData as PolingResponse}
                setStatus={setStatus}
              />
            )}
          </div>
        </div>
      </Drawer>
      <UnsavedChangesModal
        open={askQuit}
        onConfirm={() => handleCloseDrawer()}
        onCancel={() => setAskQuit(false)}
      />
    </>
  );
};
